import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, Check, ArrowRight } from "lucide-react";

const TREAT_BADGE = {
  wrap: { label: "Wrap", cls: "bg-[color:var(--info)]/15 text-[color:var(--info)]" },
  rewrite: { label: "Rewrite", cls: "bg-[color:var(--accent)]/15 text-[color:var(--accent)]" },
  replatform: { label: "Re-platform", cls: "bg-[color:var(--warning)]/15 text-[color:var(--warning)]" },
};

export default function Prescribe() {
  const navigate = useNavigate();
  const [phases, setPhases] = useState([]);
  const [openPhase, setOpenPhase] = useState(null);
  const [stacks, setStacks] = useState({ options: [], selected: null });
  const [treatments, setTreatments] = useState([]);
  // ROI state — no hardcoded defaults; derived from scan on mount
  const [roiDefaults, setRoiDefaults] = useState(null);
  const [sysN, setSysN] = useState(0);
  const [maintK, setMaintK] = useState(0);
  const [riskK, setRiskK] = useState(0);
  const [savings, setSavings] = useState(0);

  const load = async () => {
    const [ph, st, tr, roi] = await Promise.all([
      api.get("/api/prescribe/roadmap"),
      api.get("/api/prescribe/stacks"),
      api.get("/api/prescribe/treatments"),
      api.get("/api/prescribe/roi/defaults"),
    ]);
    setPhases(ph.data);
    setStacks(st.data);
    setTreatments(tr.data);
    setRoiDefaults(roi.data);
    setSysN(roi.data.systems);
    setMaintK(roi.data.maint_per_system_k);
    setRiskK(roi.data.risk_cost_k);
  };
  useEffect(() => { load(); }, []);

  // Recompute savings whenever sliders change
  useEffect(() => {
    if (roiDefaults === null) return;
    (async () => {
      try {
        const { data } = await api.post("/api/prescribe/roi", {
          systems: sysN, maint_per_system_k: maintK, risk_cost_k: riskK,
        });
        setSavings(data.savings_3yr);
      } catch {}
    })();
  }, [sysN, maintK, riskK, roiDefaults]);

  const selectStack = async (id) => {
    setStacks((s) => ({ ...s, selected: id }));
    try {
      await api.post("/api/prescribe/stacks/select", { stack_id: id });
      toast.success("Stack selected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset prescribe — clear stack selection?")) return;
    try {
      await api.delete("/api/prescribe/reset");
      await load();
      setOpenPhase(null);
      toast.success("Prescribe reset");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">/ stage · 03 — prescribe</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight">
            AI-Generated Modernization Roadmap
          </h1>
          <p className="mt-2 text-[color:var(--text-2)] max-w-3xl">
            Revora proposes a strangler-fig decomposition — extracting bounded
            contexts into cloud-native services around the monolith, with each
            phase sequenced by risk and dependency. Critically: Revora extracts{" "}
            <i>what</i> each system does into a stack-neutral spec —{" "}
            <b>your client chooses the target stack</b>, and not every system
            should be rewritten at all.
          </p>
        </div>
        <button
          data-testid="reset-prescribe-btn"
          onClick={reset}
          className="inline-flex items-center gap-2 border border-[color:var(--border-strong)] h-11 px-4 hover:border-[color:var(--critical)] hover:text-[color:var(--critical)] transition-colors duration-150 mono text-xs uppercase tracking-widest"
        >
          <RotateCcw size={14} /> Reset
        </button>
      </div>

      {/* Roadmap phases */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-lg font-medium tracking-tight">
            Roadmap — Modernization Plan
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Click a phase to expand
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {phases.map((p) => (
            <button
              key={p.order}
              data-testid={`phase-card-${p.order}`}
              onClick={() => setOpenPhase(openPhase === p.order ? null : p.order)}
              className={`text-left p-4 border transition-colors duration-150 ${
                openPhase === p.order
                  ? "border-[color:var(--accent)] bg-[color:var(--bg-2)]"
                  : "border-[color:var(--border)] bg-[color:var(--bg-2)] hover:border-[color:var(--accent)]/60"
              }`}
            >
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)]">
                Phase {p.code}
              </div>
              <div className="mt-2 text-sm font-medium">{p.title}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1">
                {p.window}
              </div>
            </button>
          ))}
        </div>
        {openPhase !== null && (
          <div
            data-testid={`phase-detail-${openPhase}`}
            className="mt-4 border border-[color:var(--accent)] bg-[color:var(--bg-2)] p-5 text-sm text-[color:var(--text-2)] leading-relaxed"
          >
            <p className="mb-2">
              <b className="text-white">Goal:</b> {phases[openPhase]?.goal}
            </p>
            <p className="mb-2">
              <b className="text-white">Key activities:</b> {phases[openPhase]?.activities}
            </p>
            <p>
              <b className="text-white">Exit criteria:</b> {phases[openPhase]?.exit_criteria}
            </p>
          </div>
        )}
      </section>

      {/* Stack-Neutral IR Diagram */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-lg font-medium tracking-tight">
            Stack-Neutral Architecture — The IR Layer
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Business logic ≠ target language
          </div>
        </div>
        <p className="text-sm text-[color:var(--text-2)] leading-relaxed mb-5">
          Scan & Diagnose don't produce "translated code" — they produce a{" "}
          <b className="text-white">stack-neutral spec</b> (business rules, data
          models, API contracts). A separate, swappable <b>adapter</b> renders
          that spec into whichever stack the client owns. Add a new stack by
          writing one new adapter — existing ones are untouched.
        </p>
        <IRDiagram />
      </section>

      {/* Stack Fit */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-lg font-medium tracking-tight">
            Stack Fit — the Client Chooses
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Click a stack to select it
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {stacks.options.map((s) => {
            const selected = stacks.selected === s.id;
            return (
              <button
                key={s.id}
                data-testid={`stack-${s.id}`}
                onClick={() => selectStack(s.id)}
                className={`text-left p-4 border transition-colors duration-150 ${
                  selected
                    ? "border-[color:var(--accent)] bg-[color:var(--bg-2)]"
                    : "border-[color:var(--border)] bg-[color:var(--bg-2)] hover:border-[color:var(--accent)]/60"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">{s.name}</span>
                  {selected && (
                    <span className="mono text-[10px] uppercase tracking-widest text-[color:var(--accent)] flex items-center gap-1">
                      <Check size={12} /> selected
                    </span>
                  )}
                </div>
                {Object.entries(s.metrics).map(([k, v]) => (
                  <div key={k} className="mb-2">
                    <div className="flex justify-between mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)] mb-1">
                      <span>{k}</span>
                      <span>{v}</span>
                    </div>
                    <div className="h-1.5 bg-[color:var(--bg-base)]">
                      <div
                        className="h-1.5 bg-[color:var(--accent)]"
                        style={{ width: `${v}%` }}
                      />
                    </div>
                  </div>
                ))}
              </button>
            );
          })}
        </div>
        <div
          data-testid="stack-fit-summary"
          className="border border-[color:var(--border)] bg-[color:var(--bg-2)] p-4 text-sm text-[color:var(--text-2)] leading-relaxed"
        >
          {stacks.selected ? (
            <>
              Selected:{" "}
              <b className="text-[color:var(--accent)]">
                {stacks.options.find((s) => s.id === stacks.selected)?.name}
              </b>{" "}
              — this becomes the target for every "Rewrite" adapter in the
              roadmap. IVOROVI builds to it; the client's own team owns and runs
              it after handoff. Nothing here forces a stack IVOROVI prefers.
            </>
          ) : (
            <>
              No stack selected yet. Click a card above — IVOROVI will target that
              stack for every "Rewrite" adapter in the roadmap.
            </>
          )}
        </div>
        {stacks.selected && (
          <div className="flex justify-end mt-3">
            <button
              data-testid="proceed-to-transform-btn"
              onClick={() => navigate("/transform")}
              className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black h-11 px-6 hover:bg-[color:var(--accent-hover)] mono text-xs uppercase tracking-widest transition-colors duration-150"
            >
              Proceed to Transform <ArrowRight size={14} />
            </button>
          </div>
        )}
      </section>

      {/* Per-System Treatment Plan */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-lg font-medium tracking-tight">
            Per-System Treatment Plan
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Not everything gets rewritten
          </div>
        </div>
        {treatments.length === 0 ? (
          <div className="mono text-xs text-[color:var(--text-3)] p-2">
            Run a Scan first to populate the treatment plan.
          </div>
        ) : (
          <div className="divide-y divide-[color:var(--border)]">
            {treatments.map((t) => {
              const b = TREAT_BADGE[t.treatment] || TREAT_BADGE.replatform;
              return (
                <div key={t.id} data-testid={`treatment-${t.id}`} className="py-3 flex gap-4 items-start">
                  <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 ${b.cls}`}>
                    {b.label}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mt-1 truncate">
                      {t.tech}
                    </div>
                    <div className="text-sm text-[color:var(--text-2)] mt-2 leading-snug">
                      {t.reason}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ROI Calculator */}
      <section className="border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <div className="flex items-baseline justify-between mb-6">
          <div className="text-lg font-medium tracking-tight">
            Modernization ROI Calculator
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)]">
            Drag to model your own estate
          </div>
        </div>
        {roiDefaults?.systems === 0 ? (
          <div className="mono text-xs text-[color:var(--text-3)] p-2">
            Run a Scan first — ROI defaults will be derived from the actual estate.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <Slider
                label="Legacy systems in scope"
                value={sysN}
                min={Math.max(roiDefaults?.systems ? 1 : 0, 0)}
                max={Math.max((roiDefaults?.systems || 0) * 4, 20)}
                step={1}
                onChange={setSysN}
                fmt={(v) => String(v)}
                tid="roi-sys"
              />
              <Slider
                label="Avg. annual maintenance cost / system"
                value={maintK}
                min={0}
                max={Math.max((roiDefaults?.maint_per_system_k || 0) * 3, 500)}
                step={10}
                onChange={setMaintK}
                fmt={(v) => `$${v}K`}
                tid="roi-maint"
              />
              <Slider
                label="Annual security-incident risk cost"
                value={riskK}
                min={0}
                max={Math.max((roiDefaults?.risk_cost_k || 0) * 3, 2000)}
                step={50}
                onChange={setRiskK}
                fmt={(v) => `$${v}K`}
                tid="roi-risk"
              />
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] pt-2 border-t border-[color:var(--border)]">
                defaults derived from {roiDefaults?.systems} scanned system{roiDefaults?.systems === 1 ? "" : "s"}
              </div>
            </div>
            <div className="border border-[color:var(--accent)] bg-[color:var(--bg-2)] p-8 text-center">
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] mb-3">
                Estimated 3-Year Net Savings
              </div>
              <div
                data-testid="roi-savings"
                className="text-5xl font-medium text-[color:var(--accent)] mono"
              >
                ${(savings / 1_000_000).toFixed(1)}M
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--text-2)] mt-4 leading-relaxed">
                Assumes 40% legacy-maintenance reduction<br />
                + 70% risk-cost reduction across the portfolio
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Slider({ label, value, min, max, step, onChange, fmt, tid }) {
  return (
    <label className="block">
      <div className="flex justify-between mb-2 mono text-xs">
        <span className="uppercase tracking-widest text-[color:var(--text-2)]">{label}</span>
        <b className="text-[color:var(--accent)]">{fmt(value)}</b>
      </div>
      <input
        data-testid={tid}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[color:var(--accent)]"
      />
    </label>
  );
}

function IRDiagram() {
  return (
    <div className="flex items-center gap-2 overflow-x-auto py-3">
      <IRCol>
        <IRBox title="Legacy Codebase" sub="per system, from Scan + Diagnose" />
      </IRCol>
      <Arrow />
      <IRCol>
        <IRBox title="Stack-Neutral IR" sub="Business rules · Data models · API contracts" tone="core" />
        <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--text-3)] text-center">
          ↓ if performance-critical
        </div>
        <IRBox title="Wrap Decision" sub="Keep native, expose via API facade — permanently" tone="wrap" />
      </IRCol>
      <Arrow />
      <IRCol>
        <IRBox title="Java · Spring Adapter" small />
        <IRBox title=".NET Core Adapter" small />
        <IRBox title="Node · NestJS Adapter" small />
        <IRBox title="+ new adapter, on demand" small dim />
      </IRCol>
      <Arrow />
      <IRCol>
        <IRBox title="Client-Owned Codebase" sub="Generated fresh in the target stack" tone="result" />
      </IRCol>
    </div>
  );
}

function IRCol({ children }) {
  return <div className="flex flex-col gap-2 min-w-[180px] shrink-0">{children}</div>;
}

function Arrow() {
  return <span className="text-xl text-[color:var(--text-3)] shrink-0">→</span>;
}

function IRBox({ title, sub, tone, small, dim }) {
  const cls =
    tone === "core"
      ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10"
      : tone === "wrap"
      ? "border-[color:var(--info)] bg-[color:var(--info)]/10"
      : tone === "result"
      ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10"
      : "border-[color:var(--border)] bg-[color:var(--bg-2)]";
  return (
    <div
      className={`border p-3 text-center ${cls} ${dim ? "opacity-55 border-dashed" : ""}`}
    >
      <div className={small ? "text-xs font-medium" : "text-sm font-semibold"}>
        {title}
      </div>
      {sub && (
        <div className="mono text-[9.5px] text-[color:var(--text-2)] mt-1 leading-snug">
          {sub}
        </div>
      )}
    </div>
  );
}
